import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { User as UserEntity, SavedPassenger, UserLoyaltyAccount } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  UserRound, Users, Plus, Pencil, Trash2, Check, Loader2, Lock,
  CalendarDays, Plane, BadgeCheck, Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import SavedPassengerForm from '../components/profile/SavedPassengerForm';
import FrequentFlyerPrograms from '../components/profile/FrequentFlyerPrograms';
import DeferaCreditsSection from '../components/profile/DeferaCreditsSection';
import DeleteAccountCard from '../components/profile/DeleteAccountCard';
import NativeAppSettingsCard from '../components/profile/NativeAppSettingsCard';
import LinkedCardsCard from '@/components/rewards/LinkedCardsCard';
import { CARD_LINKED_MILES_ENABLED } from '@/utils/cardLinkedMilesFlag';
import { getProgramLabel } from '@/constants/loyaltyPrograms';
import { REWARDS_SECTION_CARD, BRAND_PRIMARY_BTN_CLASS } from '@/constants/brandColors';
import MyVipBadge from '@/components/vip/MyVipBadge';
import { toast } from 'sonner';

// Same input chrome as the checkout PassengerForm so profile + checkout
// read as one surface.
const FIELD_BASE =
  'bg-white/[0.03] border-white/10 text-white placeholder:text-white/35 h-10 rounded-xl text-base md:text-sm font-normal px-3.5 transition-colors hover:bg-white/[0.05] focus-visible:ring-1 focus-visible:ring-[hsl(228,35%,58%)]/30 focus-visible:ring-offset-0 focus-visible:border-[hsl(228,35%,58%)]/30';

const FIELD_LABEL = 'text-[11px] font-normal text-white/55 tracking-wide';

// born_on arrives as 'YYYY-MM-DD' — parse the parts directly so the local
// timezone can't shift the date backwards a day.
function formatDob(dob) {
  if (!dob) return null;
  const [y, m, d] = String(dob).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[m - 1]} ${d}, ${y}`;
}

function formatMemberSince(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function HeroChip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-800/45 bg-brand-900/30 px-3 py-1 text-[11px] font-normal text-white/65">
      <Icon className="w-3 h-3 text-[hsl(228,35%,58%)]" />
      {children}
    </span>
  );
}

function CardHeader({ icon: Icon, title, caption, action }) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-5 border-b border-white/[0.06]">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-[hsl(228,30%,72%)]" />
        </div>
        <div className="min-w-0">
          <h3 className="text-white text-[15px] font-normal tracking-tight">{title}</h3>
          {caption && <p className="text-white/40 text-[11px] font-normal">{caption}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

export default function Profile() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved

  const [savedPassengers, setSavedPassengers] = useState([]);
  const [editingPassenger, setEditingPassenger] = useState(null);
  const [isPassengerFormOpen, setIsPassengerFormOpen] = useState(false);
  const [isSavingPassenger, setIsSavingPassenger] = useState(false);
  const [passengerError, setPassengerError] = useState(null);

  const [loyaltyAccounts, setLoyaltyAccounts] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const currentUser = await UserEntity.me();
        if (!currentUser) {
          return;
        }
        setUser(currentUser);
        setFirstName(currentUser.firstName || '');
        setLastName(currentUser.lastName || '');

        const [passengers, accounts] = await Promise.all([
          SavedPassenger.filter({ created_by: currentUser.email }).catch((error) => {
            console.error('Failed to load saved passengers:', error);
            return [];
          }),
          UserLoyaltyAccount.list().catch((error) => {
            // Table ships with migration 093 — degrade to an empty wallet
            // rather than blanking the page if it hasn't been applied yet.
            console.error('Failed to load loyalty accounts:', error);
            return [];
          }),
        ]);
        setSavedPassengers(passengers);
        setLoyaltyAccounts(accounts);
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSave = async () => {
    setSaveState('saving');
    try {
      await UserEntity.updateMyUserData({
        first_name: firstName,
        last_name: lastName,
      });
      setUser((prev) => ({ ...prev, firstName, lastName }));
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch (error) {
      console.error('Failed to save profile:', error);
      setSaveState('idle');
    }
  };

  const handlePassengerSave = async (passengerData) => {
    setIsSavingPassenger(true);
    setPassengerError(null);

    try {
      if (editingPassenger) {
        const updatedPassenger = await SavedPassenger.update(editingPassenger.id, passengerData);
        setSavedPassengers(savedPassengers.map((p) => p.id === editingPassenger.id ? updatedPassenger : p));
      } else {
        const newPassenger = await SavedPassenger.create(passengerData);
        setSavedPassengers([...savedPassengers, newPassenger]);
      }

      closePassengerForm();
    } catch (error) {
      console.error('Error saving passenger:', error);
      // The raw database message ("new row violates row-level security…")
      // meant nothing to a customer.
      setPassengerError("We couldn't save this traveler. Please try again.");
    } finally {
      setIsSavingPassenger(false);
    }
  };

  const handleEditPassenger = (passenger) => {
    setEditingPassenger(passenger);
    setIsPassengerFormOpen(true);
  };

  const handleDeletePassenger = async (passengerId) => {
    try {
      await SavedPassenger.delete(passengerId);
      setSavedPassengers(savedPassengers.filter((p) => p.id !== passengerId));
    } catch (error) {
      console.error('Failed to delete passenger:', error);
      toast.error("We couldn't remove this traveler. Please try again.");
    }
  };

  const openNewPassengerForm = () => {
    setEditingPassenger(null);
    setIsPassengerFormOpen(true);
  };

  const closePassengerForm = () => {
    setIsPassengerFormOpen(false);
    setEditingPassenger(null);
  };

  const handleLoyaltySave = async (payload) => {
    const saved = await UserLoyaltyAccount.upsert(payload);
    setLoyaltyAccounts((prev) => {
      const index = prev.findIndex((a) => a.program_key === saved.program_key);
      if (index >= 0) {
        const next = [...prev];
        next[index] = saved;
        return next;
      }
      return [...prev, saved];
    });
  };

  const handleLoyaltyDelete = async (id) => {
    await UserLoyaltyAccount.delete(id);
    setLoyaltyAccounts((prev) => prev.filter((a) => a.id !== id));
  };

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center gap-2.5 p-16 text-white/50 font-normal">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading profile…
      </div>
    );
  }

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const initials = ((user.firstName?.[0] || '') + (user.lastName?.[0] || '')).toUpperCase()
    || (user.email?.[0] || '?').toUpperCase();
  const memberSince = formatMemberSince(user.createdAt);
  const profileIncomplete = !user.firstName || !user.lastName;

  return (
    <div className="pb-16">
      <div className="max-w-4xl mx-auto px-6 md:px-8 py-8">
        {/* Masthead */}
        <div className="space-y-2 mb-8">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(228,35%,58%)]/85 font-medium">
            Account
          </p>
          <h2 className="text-2xl md:text-[32px] font-light text-white tracking-tight leading-none">
            My Profile
          </h2>
          <p className="text-white/60 text-[13px] font-normal leading-snug max-w-md">
            Your details, travelers, and loyalty programs — set once, applied to every booking.
          </p>
        </div>

        {profileIncomplete && (
          <div className="flex items-start gap-3 rounded-2xl border border-[hsl(228,35%,58%)]/25 bg-[hsl(228,40%,38%)]/[0.10] px-4 py-3.5 mb-6">
            <Sparkles className="w-4 h-4 mt-0.5 text-[hsl(228,35%,58%)] flex-shrink-0" />
            <div>
              <h3 className="text-white text-sm font-normal">Welcome to Defera!</h3>
              <p className="text-[13px] text-white/65 font-normal">Complete your name below to finish setting up your account.</p>
            </div>
          </div>
        )}

        {/* Identity hero */}
        <div className={cn(REWARDS_SECTION_CARD, 'mb-6')}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 px-5 sm:px-7 py-6 sm:py-7">
            <div className="w-16 h-16 rounded-full bg-gradient-to-b from-[hsl(228,55%,26%)] to-[hsl(228,67%,12%)] border border-[hsl(228,40%,34%)]/50 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12)] flex items-center justify-center flex-shrink-0">
              {user.profile_photo ? (
                <img src={user.profile_photo} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-white text-xl font-light tracking-wide">{initials}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-white text-xl font-light tracking-tight truncate">
                  {displayName || 'Add your name'}
                </h3>
                <MyVipBadge />
              </div>
              <p className="text-white/45 text-[13px] font-normal truncate">{user.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {memberSince && <HeroChip icon={CalendarDays}>Member since {memberSince}</HeroChip>}
                <HeroChip icon={Users}>
                  {savedPassengers.length} {savedPassengers.length === 1 ? 'traveler' : 'travelers'}
                </HeroChip>
                <HeroChip icon={Plane}>
                  {loyaltyAccounts.length} {loyaltyAccounts.length === 1 ? 'airline' : 'airlines'} linked
                </HeroChip>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Defera Credits — renders only when the customer has credit history */}
          <DeferaCreditsSection />

          {/* Profile details */}
          <div className={REWARDS_SECTION_CARD}>
            <CardHeader icon={UserRound} title="Profile Details" caption="How we address you across Defera" />
            <div className="p-5 sm:p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName" className={FIELD_LABEL}>First name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your first name"
                    className={FIELD_BASE}
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName" className={FIELD_LABEL}>Last name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Enter your last name"
                    className={FIELD_BASE}
                    autoComplete="family-name"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className={cn(FIELD_LABEL, 'flex items-center gap-1.5')}>
                  Email <Lock className="w-3 h-3 text-white/30" />
                </Label>
                <Input value={user.email} disabled className={cn(FIELD_BASE, 'text-white/50 disabled:opacity-100 disabled:cursor-default')} />
              </div>
              <div className="flex justify-end pt-1">
                <Button
                  onClick={handleSave}
                  disabled={saveState === 'saving'}
                  className={cn('rounded-full h-10 px-6 text-[13px]', BRAND_PRIMARY_BTN_CLASS)}
                >
                  {saveState === 'saving' ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Saving…</span>
                  ) : saveState === 'saved' ? (
                    <span className="flex items-center gap-2"><Check className="w-4 h-4" /> Saved</span>
                  ) : (
                    'Save Profile'
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Frequent flyer wallet */}
          <FrequentFlyerPrograms
            accounts={loyaltyAccounts}
            onSave={handleLoyaltySave}
            onDelete={handleLoyaltyDelete}
          />

          {/* Card-linked Miles (Fidel): link, see and remove cards. Hidden until launch. */}
          {CARD_LINKED_MILES_ENABLED && <LinkedCardsCard />}

          {/* Saved travelers */}
          <div className={REWARDS_SECTION_CARD}>
            <CardHeader
              icon={Users}
              title="Saved Travelers"
              caption="One tap to fill passenger details at checkout"
              action={
                <Button
                  type="button"
                  size="sm"
                  onClick={openNewPassengerForm}
                  className={cn('rounded-full h-9 px-4 text-xs flex-shrink-0', BRAND_PRIMARY_BTN_CLASS)}
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add traveler
                </Button>
              }
            />
            <div className="p-5 sm:p-6">
              {savedPassengers.length > 0 ? (
                <div className="space-y-3">
                  {savedPassengers.map((p) => {
                    const fullName = [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(' ');
                    const travelerInitials = ((p.first_name?.[0] || '') + (p.last_name?.[0] || '')).toUpperCase();
                    const dob = formatDob(p.date_of_birth);
                    return (
                      <div
                        key={p.id}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-brand-800/40 bg-brand-900/20 px-4 py-3.5 transition-colors hover:border-brand-700/45"
                      >
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-[hsl(228,30%,72%)] text-[12px] font-medium tracking-wide">{travelerInitials || '?'}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-white text-sm font-normal tracking-tight truncate">{fullName || 'Unnamed traveler'}</p>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                              {dob && (
                                <span className="text-white/40 text-[11px] font-normal">{dob}</span>
                              )}
                              {p.passport_number && (
                                <span className="text-white/40 text-[11px] font-normal inline-flex items-center gap-1">
                                  <BadgeCheck className="w-3 h-3 text-[hsl(228,35%,58%)]/80" /> Passport
                                </span>
                              )}
                              {p.frequent_flyer_program && (
                                <span className="text-white/40 text-[11px] font-normal inline-flex items-center gap-1">
                                  <Plane className="w-3 h-3 text-[hsl(228,35%,58%)]/80" /> {getProgramLabel(p.frequent_flyer_program)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditPassenger(p)}
                            className="h-8 w-8 rounded-full text-white/45 hover:text-white hover:bg-white/[0.08]"
                            title="Edit traveler"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeletePassenger(p.id)}
                            className="h-8 w-8 rounded-full text-red-400/70 hover:text-red-300 hover:bg-red-500/10"
                            title="Remove traveler"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 px-4">
                  <div className="w-12 h-12 rounded-full bg-[hsl(228,40%,38%)]/[0.16] border border-[hsl(228,35%,58%)]/20 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-5 h-5 text-[hsl(228,30%,72%)]" />
                  </div>
                  <p className="text-white text-sm font-normal mb-1.5">No saved travelers yet</p>
                  <p className="text-white/60 text-[13px] font-normal max-w-sm mx-auto leading-relaxed">
                    Save the people you fly with — passport details, birthdays, and
                    preferences fill in automatically at checkout.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* iOS app only: notifications and the Face ID lock on this phone */}
          <NativeAppSettingsCard />

          {/* Apple Guideline 5.1.1(v): delete the account from inside the app */}
          <DeleteAccountCard />
        </div>

        <AnimatePresence>
          {isPassengerFormOpen && (
            <SavedPassengerForm
              passenger={editingPassenger}
              onSave={handlePassengerSave}
              onClose={closePassengerForm}
              isSaving={isSavingPassenger}
              error={passengerError}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
